import React from 'react';
import styled from 'styled-components';
import { priceOnFinish } from '../../../../supplementary_components/priceInputDelegate';
import { canSeeLoadRates, can } from '../../../../supplementary_components/userDataDelegate';
import { retrieveCompanyData } from '../../../../supplementary_components/companySettings';
import { followsWeeklyOwnerPayoutFromCompany, resolveShowDriverPayoutGross } from '../../../plannerBoardSettings';
import { useBoardStore } from '../../../boardStore';

/** Wrapper is only as tall as Gross (so grid centering is unchanged); payout floats below. */
const GrossStack = styled.div`
    position: relative;
    text-align: right;
    width: 100%;
`;

const PayoutBelow = styled.div`
    position: absolute;
    right: 0;
    top: calc(100% + 0.05rem);
    text-align: right;
    font-weight: 700;
    font-size: 0.64rem;
    color: var(--light-gray);
    line-height: 1;
    pointer-events: none;
    white-space: nowrap;
`;

const RateValue = styled.div`
    text-align:right;
    font-weight:800;
    font-size:1.1rem;
    color:${props => props.$muted ? 'var(--light-gray)' : `rgb(${props.$rgb ? props.$rgb : "0, 0, 0"})`};
`;

export const MB_GROSS = ({truckId}) => {
    const id = truckId == null ? null : String(truckId);
    const totalGross = useBoardStore((s) => (id == null ? 0 : (s.trucksTotalGross[id] || 0)));
    const truckType = useBoardStore((s) => (id == null ? null : s.trucksById[id]?.truckType));
    const driver1 = useBoardStore((s) => (id == null ? null : s.trucksById[id]?.driver1));
    const driver2 = useBoardStore((s) => (id == null ? null : s.trucksById[id]?.driver2));
    const totalPayout = useBoardStore((s) => (id == null ? 0 : (s.trucksTotalDriverPayout[id] || 0)));
    const showDriverPayoutGrossPref = useBoardStore((s) => s.showDriverPayoutGross);
    const grossHidden = useBoardStore((s) =>
        id != null &&
        s.grossVisibleTruckIds != null &&
        !s.grossVisibleTruckIds.has(id)
    );
    if (grossHidden) {
        return <RateValue $muted>-</RateValue>;
    }
    const colorObject = GrossColorGenerator(totalGross);
    const grossValue = (
        <RateValue $rgb={`${colorObject.red}, ${colorObject.green}, ${colorObject.blue}`} >
            {priceOnFinish(`${totalGross}`, true)}
        </RateValue>
    );

    const showPayoutBelow =
        resolveShowDriverPayoutGross(showDriverPayoutGrossPref, {
            userIsUpdater: !can('DRIVER_PAYOUTS', 'VIEW'),
            userCanSeeRates: canSeeLoadRates(),
            followsWeeklyOwnerPayout: followsWeeklyOwnerPayoutFromCompany(retrieveCompanyData()),
        }) &&
        truckShowsDriverPayout(truckType, driver1, driver2);

    if (!showPayoutBelow) {
        return grossValue;
    }

    return (
        <GrossStack>
            <PayoutBelow>{priceOnFinish(`${totalPayout}`, true)}</PayoutBelow>
            {grossValue}
        </GrossStack>
    );
}

export const MB_PAYOUT_GROSS = ({truckId}) => {
    const id = truckId == null ? null : String(truckId);
    const truckType = useBoardStore((s) => (id == null ? null : s.trucksById[id]?.truckType));
    const driver1 = useBoardStore((s) => (id == null ? null : s.trucksById[id]?.driver1));
    const driver2 = useBoardStore((s) => (id == null ? null : s.trucksById[id]?.driver2));
    const totalPayout = useBoardStore((s) => (id == null ? 0 : (s.trucksPayoutGross[id] || 0)));
    const payoutHidden = useBoardStore((s) =>
        id != null &&
        s.grossVisibleTruckIds != null &&
        !s.grossVisibleTruckIds.has(id)
    );
    if (payoutHidden || !truckShowsDriverPayout(truckType, driver1, driver2)) {
        return <RateValue $muted>-</RateValue>;
    }
    const colorObject = GrossColorGenerator(totalPayout);
    return (
        <RateValue $rgb={`${colorObject.red}, ${colorObject.green}, ${colorObject.blue}`}>
            {priceOnFinish(`${totalPayout}`, true)}
        </RateValue>
    );
}

const rpmDisplay = (rpm) => {
    if (rpm == null || Number.isNaN(rpm)) return null;
    return priceOnFinish(`${rpm}`);
};

export const MB_RPM = ({truckId}) => {
    const id = truckId == null ? null : String(truckId);
    const rpm = useBoardStore((s) => (id == null ? null : (s.trucksWeeklyRpm[id] ?? null)));
    const grossHidden = useBoardStore((s) =>
        id != null &&
        s.grossVisibleTruckIds != null &&
        !s.grossVisibleTruckIds.has(id)
    );
    const display = rpmDisplay(rpm);
    if (grossHidden || display == null) {
        return <RateValue $muted>-</RateValue>;
    }
    const colorObject = RPMColorGenerator(rpm);
    return (
        <RateValue $rgb={`${colorObject.red}, ${colorObject.green}, ${colorObject.blue}`}>
            {display}
        </RateValue>
    );
};

export const MB_PAYOUT_RPM = ({truckId}) => {
    const id = truckId == null ? null : String(truckId);
    const truckType = useBoardStore((s) => (id == null ? null : s.trucksById[id]?.truckType));
    const driver1 = useBoardStore((s) => (id == null ? null : s.trucksById[id]?.driver1));
    const driver2 = useBoardStore((s) => (id == null ? null : s.trucksById[id]?.driver2));
    const rpm = useBoardStore((s) => (id == null ? null : (s.trucksPayoutRpm[id] ?? null)));
    const payoutHidden = useBoardStore((s) =>
        id != null &&
        s.grossVisibleTruckIds != null &&
        !s.grossVisibleTruckIds.has(id)
    );
    const display = rpmDisplay(rpm);
    if (payoutHidden || !truckShowsDriverPayout(truckType, driver1, driver2) || display == null) {
        return <RateValue $muted>-</RateValue>;
    }
    const colorObject = RPMColorGenerator(rpm);
    return (
        <RateValue $rgb={`${colorObject.red}, ${colorObject.green}, ${colorObject.blue}`}>
            {display}
        </RateValue>
    );
};
function truckShowsDriverPayout(truckType, driver1, driver2) {
    if (truckType == null) return false;
    if (truckType === 'Company') {
        return driver1?.paymentType === 'Percentage' || driver2?.paymentType === 'Percentage';
    }
    return true;
}

export const GrossColorGenerator = (totalGross) => {
    const min = 4000
    const max = 12000
    const p = (totalGross - min) / (max - min);
    return {
        red: 255 * (1 - p) < 100 ? 100 : 255 * (1 - p),
        green: 235 * p > 180 ? 180 : 235 * p,
        blue: 50
    }
}
export const RPMColorGenerator = (totalGross) => {
    const min = 1.5
    const max = 2.2
    const p = (totalGross - min) / (max - min);
    return {
        red: 255 * (1 - p) < 100 ? 100 : 255 * (1 - p),
        green: 235 * p > 180 ? 180 : 235 * p,
        blue: 50
    }
}
